import { Box, Button, Switch, FormControlLabel, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Localozation from "../components/Localisation";
import { useTranslation } from "react-i18next";
import DataGridFullScreen from "../components/DataGridFullScreen";
import dayjs from "dayjs";
import { instance } from "../units/interceptors";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

function WasteArchivePage() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const apiRef = useGridApiRef();

    const [startDate, setStartDate] = useState(dayjs().startOf("month"));
    const [endDate, setEndDate] = useState(dayjs().endOf("month"));
    const [serverData, setServerData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showKitchen, setShowKitchen] = useState(false);

    const fetchData = async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        try {
            const { data } = await instance.get("/api/branches/waste-totals/", {
                params: {
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                },
            });
            setServerData(Array.isArray(data) ? data : []);
        } catch {
            setServerData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = (type) => {
        const allRows = Array.from(apiRef.current.getRowModels().values());
        const cleanedRows = allRows.map(({ localEdited, edited, comment, current_quantity, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(cleanedRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const fileName = `waste_${type}_${startDate.format('YYYY-MM-DD')}_${endDate.format('YYYY-MM-DD')}.xlsx`;
        saveAs(blob, fileName);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Отфильтрованные точки по свитчу
    const filteredData = useMemo(
        () => serverData.filter(b => b.isKitchen === showKitchen),
        [serverData, showKitchen]
    );

    // Сортируем бранчи по длине названия (как ты делал)
    const branches = useMemo(
        () =>
            filteredData
                .map((b) => ({ id: b.branch_id, name: b.branch_name }))
                .sort((a, b) => a.name.length - b.name.length),
        [filteredData]
    );

    // Продукты берём из первого элемента отфильтрованных данных
    const productsRows = useMemo(() => {
        const firstBranchProducts = filteredData[0]?.products ?? [];
        return firstBranchProducts
            .filter(p => p.id_s2s ===1001)
            .map((p) => {
                const row = { id: p.id, product: p.name, total: 0 };
                filteredData.forEach((br) => {
                    console.log(br.total_quantity)
                    const found = br.products.find((x) => x.id === p.id);
                    row.total += +found.total_quantity
                    row[`b_${br.branch_id}`] = Number(found.total_quantity);
                });
                return row;
            });
    }, [filteredData]);

    const productsIngradientRows = useMemo(() => {
        const firstBranchProducts = filteredData[0]?.products ?? [];
        return firstBranchProducts
            .filter(p => p.id_s2s !== 1001)
            .map((p) => {
                const row = { id: p.id, product: p.name, total: 0 };
                filteredData.forEach((br) => {
                    console.log(br.total_quantity)
                    const found = br.products.find((x) => x.id === p.id);
                    row.total += +found.total_quantity
                    row[`b_${br.branch_id}`] = Number(found.total_quantity);
                });
                return row;
            });
    }, [filteredData]);

    const columns = useMemo(
        () => [
            {
                field: "product",
                headerName: t("grid.product_name"),
                minWidth: 220,
                flex: 1,
            },
            ...branches.map((b) => ({
                field: `b_${b.id}`,
                headerName: b.name,
                minWidth: 120,
                flex: 1,
                type: "number",
            })),
            {
                field: "total",
                headerName: 'Total',
                minWidth: 220,
                flex: 1,
            },
        ],
        [branches, t]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 5, height: "100%", width: "100%" }}>
            <Localozation>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", p: 2 }}>
                    <DatePicker
                        label={"Start Date"}
                        format="YYYY-MM-DD"
                        value={startDate}
                        onChange={(v) => setStartDate(v)}
                        disabled={loading}
                    />
                    <DatePicker
                        label={"End Date"}
                        format="YYYY-MM-DD"
                        value={endDate}
                        onChange={(v) => setEndDate(v)}
                        disabled={loading}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showKitchen}
                                onChange={(e) => setShowKitchen(e.target.checked)}
                                color="primary"
                            />
                        }
                        // Подписи слева/справа свитча
                        label={showKitchen ? "Kitchen" : "Branch"}
                    />
                </Box>
            </Localozation>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>

                <Box sx={{ width: "100%", height: "100%" }}>
                    <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <Button onClick={() => handleExport('ingradient')} variant="contained" color="primary" style={{ marginBottom: 16 }}>
                            Export to Excel
                        </Button>
                        <Typography>Բաղադրիչներ</Typography>
                    </Box>
                    <DataGridFullScreen
                        apiRef={apiRef}
                        columns={columns}
                        rows={productsIngradientRows}
                        loading={loading}
                        showCellVerticalBorder
                        hideFooter
                        pinnedColumns={{ left: ["product"] }}
                    />
                </Box>
                <Box sx={{ width: "100%", height: "100%" }}>
                    <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <Button onClick={() => handleExport('product')} variant="contained" color="primary" style={{ marginBottom: 16 }}>
                            Export to Excel
                        </Button>
                        <Typography>Վաճառքի ապրանքներ</Typography>
                    </Box>
                    <DataGridFullScreen
                        apiRef={apiRef}
                        columns={columns}
                        rows={productsRows}
                        loading={loading}
                        showCellVerticalBorder
                        hideFooter
                        pinnedColumns={{ left: ["product"] }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default WasteArchivePage;
